<template>
  <BaseLayout>
    <div>
      <div class="header-bar">
        <MainMenu />
      </div>

      <section v-if="showTenantBookingWidget" class="tenant-booking-rail">
        <AppointmentBookingWidget />
      </section>

      <div id="main-content" class="container mt-0" tabindex="-1">
        <router-view />
      </div>

      <SiteFooter v-if="!isSingleScreenHome" />

      <ServiceSeoDirector />
    </div>
  </BaseLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import BaseLayout from '../BaseLayout.vue';
import MainMenu from './MainMenu.vue';
import SiteFooter from '@/components/website/SiteFooter.vue';
import ServiceSeoDirector from '@/components/seo/ServiceSeoDirector.vue';
import AppointmentBookingWidget from '@/components/scheduling/AppointmentBookingWidget.vue';
import { loadBranding } from '@/services/branding.service';

const route = useRoute();

// The platform home is a single-screen booking page: the menu stays visible so
// the apex is navigable, but the footer keeps out of the way there.
const isSingleScreenHome = computed(() => route.name === 'home');

// The star scheduling widget is the tenant's primary call to action, so it sits
// directly under the navbar on every tenant page instead of only on `/`.
//
// Two exclusions, both deliberate:
//   - the apex does not get it: the platform is a booking directory, and a
//     tenant widget on it would offer a single clinic's agenda as if it were
//     the platform's own;
//   - the home route does not get it from here, because HomePage already
//     renders the widget and two copies would stack on the same screen.
const isTenant = computed(() => !loadBranding().isPlatform);
const showTenantBookingWidget = computed(() => isTenant.value && !isSingleScreenHome.value);
</script>

<style scoped>
/* Same treatment as the home page section so the widget reads as one component
   whether it was reached from `/` or from any other tenant page. */
.tenant-booking-rail {
  background: linear-gradient(180deg, #f7f5fb 0%, #efe9f7 100%);
  padding: clamp(1.5rem, 3.5vw, 2.5rem) clamp(1rem, 5vw, 2rem);
  display: flex;
  justify-content: center;
}

@media (max-width: 720px) {
  .tenant-booking-rail {
    padding: 1.25rem 0.75rem;
  }
}
</style>
